import type { TalentRedactionFields, WithheldKey } from "components/Screens/Marketplace/Customer/types";
import getTalentFullName from "components/Screens/Marketplace/Customer/utils/talentName";
import avatarFemale from "components/Screens/Marketplace/Customer/assets/common/avatar-female.svg";
import avatarFemale2 from "components/Screens/Marketplace/Customer/assets/common/avatar-female-2.svg";
import avatarFemale3 from "components/Screens/Marketplace/Customer/assets/common/avatar-female-3.svg";
import avatarFemale4 from "components/Screens/Marketplace/Customer/assets/common/avatar-female-4.svg";
import avatarMale from "components/Screens/Marketplace/Customer/assets/common/avatar-male.svg";
import avatarMale2 from "components/Screens/Marketplace/Customer/assets/common/avatar-male-2.svg";
import avatarMale3 from "components/Screens/Marketplace/Customer/assets/common/avatar-male-3.svg";
import avatarMale4 from "components/Screens/Marketplace/Customer/assets/common/avatar-male-4.svg";
import avatarMale5 from "components/Screens/Marketplace/Customer/assets/common/avatar-male-5.svg";

export function isRedacted(talent?: TalentRedactionFields | null): boolean {
  const level = talent?.redaction?.level;
  return !!level && level !== "none";
}

export function isWithheld(talent: TalentRedactionFields | null | undefined, key: WithheldKey): boolean {
  return !!talent?.redaction?.withheld?.includes(key);
}

// The prototype's DiceBear "notionists" avatars, bundled (product 2026-10-27: four for women, five for men).
const AVATARS = {
  female: [avatarFemale, avatarFemale2, avatarFemale3, avatarFemale4],
  male: [avatarMale, avatarMale2, avatarMale3, avatarMale4, avatarMale5],
} as const;

/** A stable index from the talent's id (prototype avColor hash): the same person always gets the same avatar. */
function pick<T>(pool: readonly T[], id: string | undefined): T {
  let h = 0;
  for (const c of id || "") h = (h * 31 + c.charCodeAt(0)) >>> 0;
  return pool[h % pool.length];
}

/**
 * The picture to draw for a talent: the photo, or where the photo is withheld an illustrated avatar of the gender
 * the portal sent (`avatar`), one of several, picked by the talent's id. Undefined = draw initials.
 */
export function talentPicture(
  talent: (TalentRedactionFields & { _id?: string; profilePicture?: string | null }) | null | undefined
): string | undefined {
  if (!talent) return undefined;
  if (isWithheld(talent, "profilePicture")) return talent.avatar ? pick(AVATARS[talent.avatar], talent._id) : undefined;
  return talent.profilePicture || undefined;
}

type NameSource = TalentRedactionFields & { firstName?: string; middleName?: string | null; lastName?: string | null };

/** "Muhammad U." for a withheld surname (prototype `displayName`), the full name otherwise. */
export function getTalentDisplayName(talent: NameSource | null | undefined): string {
  if (!talent) return "";
  if (isWithheld(talent, "lastName")) {
    const initial = talent.lastInitial ? `${talent.lastInitial}.` : "";
    return `${(talent.firstName || "").trim()} ${initial}`.trim();
  }
  return getTalentFullName(talent as any);
}
